import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Menu,
    Bell,
    Sun,
    Moon,
    LogOut,
    User,
    ChevronDown
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

export const Topbar = ({ onMenuClick }) => {
    const { user, logout, api } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [seenIds, setSeenIds] = useState(new Set());

    useEffect(() => {
        let mounted = true;
        let firstRun = true;
        const fetchNotifications = async () => {
            try {
                const response = await api.get('/notifications?unread_only=true');
                if (!mounted) return;
                setNotifications(response.data.slice(0, 5));
                setUnreadCount(response.data.length);
                // Toast on truly-new notifications (skip the initial load)
                if (!firstRun) {
                    setSeenIds(prevSeen => {
                        const next = new Set(prevSeen);
                        for (const n of response.data) {
                            if (!prevSeen.has(n.id)) {
                                next.add(n.id);
                                if (n.type === 'post_published') {
                                    toast.success(`${n.title}: ${n.message}`);
                                } else if (n.type === 'post_failed' || n.type === 'post_partial') {
                                    toast.error(`${n.title}: ${n.message}`);
                                } else {
                                    toast(`${n.title}: ${n.message}`);
                                }
                            }
                        }
                        return next;
                    });
                } else {
                    setSeenIds(new Set(response.data.map(n => n.id)));
                    firstRun = false;
                }
            } catch (error) {
                console.error('Failed to fetch notifications:', error);
            }
        };
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 20000);
        return () => { mounted = false; clearInterval(interval); };
    }, [api]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const getInitials = (name) => {
        return name
            ?.split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || 'U';
    };

    return (
        <header
            className="sticky top-0 z-20 w-full h-16 border-b border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60"
            data-testid="topbar"
        >
            <div className="flex items-center justify-between h-full px-4 lg:px-6">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="lg:hidden"
                        onClick={onMenuClick}
                        data-testid="menu-toggle-btn"
                    >
                        <Menu className="h-5 w-5" />
                    </Button>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleTheme}
                        data-testid="theme-toggle-btn"
                    >
                        {theme === 'dark' ? (
                            <Sun className="h-5 w-5" />
                        ) : (
                            <Moon className="h-5 w-5" />
                        )}
                    </Button>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="relative"
                                data-testid="notifications-btn"
                            >
                                <Bell className="h-5 w-5" />
                                {unreadCount > 0 && (
                                    <span className="notification-dot" />
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-80">
                            <DropdownMenuLabel className="flex items-center justify-between">
                                Notifications
                                {unreadCount > 0 && (
                                    <Badge variant="secondary">{unreadCount}</Badge>
                                )}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {notifications.length > 0 ? (
                                notifications.map((notif) => (
                                    <DropdownMenuItem
                                        key={notif.id}
                                        className="flex flex-col items-start gap-1 py-3"
                                    >
                                        <span className="font-medium">{notif.title}</span>
                                        <span className="text-sm text-muted-foreground">
                                            {notif.message}
                                        </span>
                                    </DropdownMenuItem>
                                ))
                            ) : (
                                <div className="p-4 text-center text-muted-foreground">
                                    No new notifications
                                </div>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                className="flex items-center gap-2 px-2"
                                data-testid="user-menu-btn"
                            >
                                <Avatar className="h-8 w-8">
                                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                                        {getInitials(user?.name)}
                                    </AvatarFallback>
                                </Avatar>
                                <span className="hidden md:inline-block font-medium">
                                    {user?.name}
                                </span>
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel>
                                <div className="flex flex-col">
                                    <span>{user?.name}</span>
                                    <span className="text-sm font-normal text-muted-foreground">
                                        {user?.email}
                                    </span>
                                </div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate('/settings')}>
                                <User className="mr-2 h-4 w-4" />
                                Profile Settings
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={handleLogout}
                                className="text-destructive focus:text-destructive"
                                data-testid="logout-btn"
                            >
                                <LogOut className="mr-2 h-4 w-4" />
                                Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </header>
    );
};
